import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/lib/supabaseClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Truck } from "lucide-react";

export default function Login() {
  const { signIn, session } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // If already logged in, bounce to the original target (or dashboard).
  useEffect(() => {
    if (session && session.user?.app_metadata?.must_change_password !== true) navigate(from, { replace: true });
  }, [session, from, navigate]);

  const friendlyError = (err) => {
    if (!err) return "Sign-in failed. Please try again.";
    const msg = err.message || String(err);
    // Treat any non-Supabase-shaped error as a generic invalid-credentials message
    if (/invalid login credentials/i.test(msg)) return "Invalid email or password.";
    if (/email not confirmed/i.test(msg)) return "Please confirm your email before signing in.";
    if (/body stream already read|failed to execute|fetch/i.test(msg)) {
      return "Invalid email or password.";
    }
    return msg;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const { data, error: err } = await signIn(email, password);
      if (err) {
        setError(friendlyError(err));
        return;
      }
      const meta = data?.user?.app_metadata || {};
      if (meta.must_change_password === true) {
        const exp = Date.parse(meta.temp_password_expires_at || "");
        if (!Number.isFinite(exp) || Date.now() >= exp) {
          await supabase.auth.signOut({ scope: "local" });
          setError("Your temporary password has expired. Ask your administrator for a new one.");
          return;
        }
        navigate("/reset-password", { replace: true, state: { email } });
        return;
      }
      navigate(from, { replace: true });
    } catch (ex) {
      setError(friendlyError(ex));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="login-page"
      className="min-h-screen w-full bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 flex items-center justify-center px-4 py-12"
    >
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="rounded-xl bg-white/10 backdrop-blur p-3 ring-1 ring-white/20">
            <Truck className="h-7 w-7 text-white" strokeWidth={1.5} />
          </div>
          <div className="text-white">
            <div className="text-xl font-semibold tracking-tight">timeslot.au</div>
            <div className="text-xs text-white/70 -mt-0.5">Logistics Booking System</div>
          </div>
        </div>

        <Card className="border-white/10 bg-white/95 backdrop-blur shadow-2xl">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl">Sign in</CardTitle>
            <CardDescription>
              Use your work email and password to access the dock manager.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
              {error && (
                <Alert variant="destructive" data-testid="login-error">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email-input"
                  placeholder="you@company.com"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <Link
                    to="/forgot-password"
                    className="text-xs text-blue-600 hover:underline"
                    data-testid="forgot-password-link"
                  >
                    Forgot?
                  </Link>
                </div>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full"
                disabled={submitting}
                data-testid="login-submit-btn"
              >
                {submitting ? "Signing in…" : "Sign in"}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-white/60 mt-6">
          Need access? Ask your site administrator to invite you.
        </p>
      </div>
    </div>
  );
}
